import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Send, Image, Dog, User, Phone, Video,
  MoreVertical, Check, CheckCheck, Smile
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Conversation List Component
const ConversationList = ({ conversations, onSelect, selectedId }) => {
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <Dog className="w-16 h-16 text-[#7A7571]/30 mb-4" />
        <h3 className="text-lg font-semibold text-[#1A1817] mb-2">No messages yet</h3>
        <p className="text-sm text-[#7A7571]">Match with other pups to start chatting!</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#EBE8E0]">
      {conversations.map(conv => (
        <button
          key={conv.id}
          onClick={() => onSelect(conv)}
          className={`w-full p-4 flex items-center gap-3 hover:bg-[#F5F3EE] transition text-left ${
            selectedId === conv.id ? 'bg-[#FF7555]/5' : ''
          }`}
        >
          <div className="relative">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F5F3EE]">
              {conv.dog2?.photos?.[0] ? (
                <img src={conv.dog2.photos[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Dog className="w-7 h-7 text-[#7A7571]" />
                </div>
              )}
            </div>
            {/* Online indicator */}
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-[#4E876C] border-2 border-white rounded-full" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-[#1A1817] truncate">
                {conv.dog2?.name || conv.other_user?.name || 'Unknown'}
              </h3>
              <span className="text-xs text-[#7A7571]">{timeAgo(conv.last_message_at)}</span>
            </div>
            <p className="text-sm text-[#7A7571] truncate">
              {conv.last_message_preview || 'Start the conversation!'}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

// Chat View Component
const ChatView = ({ conversation, user, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (conversation) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/puppoto/conversations/${conversation.id}/messages`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    const messageText = newMessage;
    setNewMessage('');
    setSending(true);

    try {
      const res = await fetch(`${API_URL}/api/puppoto/conversations/${conversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: messageText })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
      }
    } catch (error) {
      toast.error('Failed to send');
      setNewMessage(messageText);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const otherDog = conversation.dog2;
  const otherUser = conversation.other_user;

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b border-[#EBE8E0] bg-white">
        <button onClick={onBack} className="lg:hidden p-2 -ml-2 hover:bg-[#F5F3EE] rounded-full">
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F3EE]">
          {otherDog?.photos?.[0] ? (
            <img src={otherDog.photos[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Dog className="w-5 h-5 text-[#7A7571]" />
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="font-semibold text-[#1A1817]">{otherDog?.name || 'Unknown'}</h3>
          <p className="text-xs text-[#7A7571]">{otherDog?.breed} • {otherUser?.name}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
            <Phone className="w-5 h-5 text-[#7A7571]" />
          </button>
          <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
            <Video className="w-5 h-5 text-[#7A7571]" />
          </button>
          <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
            <MoreVertical className="w-5 h-5 text-[#7A7571]" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FDFBF7]">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin w-6 h-6 border-2 border-[#FF7555] border-t-transparent rounded-full" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-[#7A7571]">Say hi to {otherDog?.name}! 🐕</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isMe = msg.sender_id === user?.user_id;
            const showAvatar = !isMe && (idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id);
            
            return (
              <div
                key={msg.id}
                className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                {!isMe && showAvatar && (
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                    {msg.sender_picture ? (
                      <img src={msg.sender_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-4 h-4 text-[#7A7571]" />
                      </div>
                    )}
                  </div>
                )}
                {!isMe && !showAvatar && <div className="w-8" />}
                
                <div
                  className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${
                    isMe
                      ? 'bg-[#FF7555] text-white rounded-br-md'
                      : 'bg-white text-[#1A1817] rounded-bl-md shadow-sm'
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                </div>
                
                {isMe && (
                  <div className="flex items-center text-[#7A7571]">
                    {msg.read ? (
                      <CheckCheck className="w-4 h-4 text-[#4E876C]" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-[#EBE8E0] bg-white">
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
            <Image className="w-5 h-5 text-[#7A7571]" />
          </button>
          <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
            <Smile className="w-5 h-5 text-[#7A7571]" />
          </button>
          
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 rounded-full border-[#EBE8E0]"
            onKeyPress={e => e.key === 'Enter' && handleSend()}
          />
          
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="w-10 h-10 rounded-full bg-[#FF7555] hover:bg-[#E5684A] p-0"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Messages Component
export default function PupMessages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      // Check auth
      try {
        const authRes = await fetch(`${API_URL}/api/puppoto/auth/me`, {
          credentials: 'include'
        });
        
        if (!authRes.ok) {
          navigate('/puppoto', { replace: true });
          return;
        }
        
        const userData = await authRes.json();
        setUser(userData);
        
        // Fetch conversations
        const convsRes = await fetch(`${API_URL}/api/puppoto/conversations`, {
          credentials: 'include'
        });
        
        if (convsRes.ok) {
          const convsData = await convsRes.json();
          setConversations(convsData.conversations || []);
          
          // Select conversation if ID in URL
          if (conversationId) {
            const conv = convsData.conversations?.find(c => c.id === conversationId);
            if (conv) setSelectedConv(conv);
          }
        }
      } catch (error) {
        navigate('/puppoto', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [conversationId]);

  const handleSelectConversation = (conv) => {
    setSelectedConv(conv);
    navigate(`/puppoto/messages/${conv.id}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FF7555] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#EBE8E0]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <h1 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Messages
          </h1>
          
          <button 
            onClick={() => navigate('/puppoto/profile')}
            className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F3EE]"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-5 h-5 text-[#7A7571]" />
              </div>
            )}
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex max-w-4xl mx-auto w-full">
        {/* Conversations List - Hidden on mobile when chat is open */}
        <div className={`w-full lg:w-80 border-r border-[#EBE8E0] ${selectedConv ? 'hidden lg:block' : 'block'}`}>
          <ConversationList
            conversations={conversations}
            onSelect={handleSelectConversation}
            selectedId={selectedConv?.id}
          />
        </div>

        {/* Chat View */}
        <div className={`flex-1 ${selectedConv ? 'block' : 'hidden lg:flex lg:items-center lg:justify-center'}`}>
          {selectedConv ? (
            <ChatView
              conversation={selectedConv}
              user={user}
              onBack={() => {
                setSelectedConv(null);
                navigate('/puppoto/messages', { replace: true });
              }}
            />
          ) : (
            <div className="text-center text-[#7A7571]">
              <Dog className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
